import { Injectable } from '@angular/core';

import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';

import {
  FileFileData,
  MediaAudioData,
  MediaImageData,
  MediaRemoteVideoData,
  MediaVideoData,
  ParagraphTextMediaBoxData,
} from '@generated/content';

import { CmsComponentConfig, TextMediaBox } from '@interfaces/config';
import { CmsComponentNodeData } from '@interfaces/cms-component-node-data';
import { TMediaNodeType } from '@interfaces/media';
import { CmsFixes } from '@interfaces/_fixes';
import { MediaResolver } from '@services/resolvers/auxiliary/media-resolver';
import { YoutubeVideo } from '@models/classes';
import { ButtonTypeEnum, CmsComponentTypeEnum, CmsMediaTypeEnum, MediaTypesEnum } from '@models/enums';


@Injectable({ providedIn: 'root' })
export class TextMediaBoxComponentResolver extends BaseComponentResolver {
  static override instance: TextMediaBoxComponentResolver;
  private readonly componentType = TextMediaBox.TypeEnum;

  static getInstance(): TextMediaBoxComponentResolver {
    return (TextMediaBoxComponentResolver.instance ||= new TextMediaBoxComponentResolver());
  }

  public override resolveComponentConfig(
    node: ParagraphTextMediaBoxData,
    nodePageData: CmsComponentNodeData[] = []
  ): CmsComponentConfig<TextMediaBox.ComponentConfig> | null {
    if (!node) {
      return null;
    }

    //TODO: Fix this in page level to exclude the text-media-boxes that are part of paragraph--small_column_module
    var smallColModules =  nodePageData.filter(function(event){
      return event.type == 'paragraph--small_column_module';
    });
    if(smallColModules && smallColModules.length>0){
      //
      for(let i = 0; i<smallColModules.length;i++){
        let smallColModule = smallColModules[i];
        let smallColModuelRelationsips = smallColModule.relationships;
        if("field_module" in smallColModuelRelationsips){
          let smallColData = (smallColModuelRelationsips['field_module'].data);
          const index = smallColData.findIndex(element => element.id === node.id);
          if (index !== -1) {
            return null;
          }

        }
      }

    }

 

    this.nodePageData = nodePageData;

   

    const cta = this.buildCtaObject(node);
    const media = this.getMediaConfig(node);
   
    const mediaSize = node.attributes.field_media_size_mediabox === 'large' ? 'large' : 'small';
    const order = this.getOrder(node);
    const layout = this.getLayout(node);
     const mediaLink = this.getMediaConfigCta(node);
    const [title, headline, body] = this.buildTexts(node);
    const background = this.getBackground(node);
 

    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(node),
        anchor_id : node.attributes.field_anchor_id,
        title,
        headline,
        body,
        media,
        mediaSize,
        cta,
        order,
        layout,
        mediaLink,
        background,
      },
    };
  }


        private getMediaConfigCta(node: ParagraphTextMediaBoxData): {
        label: string;
        url: string;
     
      } {

        if (!node?.attributes.field_media_link) {
          return null;
        }
      
        const label = this.hyphenate(node.attributes.field_media_link.title);
        const url = node.attributes.field_media_link.url;
     
     
    
        return { label, url };
      }

  private buildCtaObject(node: ParagraphTextMediaBoxData): {
    label: string;
    url: string;
    buttonType: ButtonTypeEnum;
    target: '_self' | '_blank';
  } {
    if (!node?.attributes.field_cta_link_mediabox) {
      return null;
    }

    const label = this.hyphenate(node.attributes.field_cta_link_mediabox.title);
    const url = node.attributes.field_cta_link_mediabox.url;
    const buttonType = this.getButtonType(node);
    const target = node.attributes.field_openinnewtab === true ? '_blank' : '_self';

    return { label, url, buttonType, target };
  }

  private buildTexts(node: ParagraphTextMediaBoxData): string[] {

    const attributes = node.attributes as CmsFixes.ParagraphTextMediaBoxDataAttributes;

    const title = attributes?.field_headline_new_mediabox
      ? attributes?.field_headline_new_mediabox
      : attributes.field_title_mediabox;

      let field_sub_headline_mediabox_updated = attributes.field_sub_headline_mediabox?.processed ?? attributes.field_sub_headline_mediabox?.value;
      if(field_sub_headline_mediabox_updated){
        field_sub_headline_mediabox_updated =  field_sub_headline_mediabox_updated.replaceAll("cms.spsglobal.com","spsglobal.com");

      }

    return [title, attributes.field_headline_mediabox,field_sub_headline_mediabox_updated].map(this.hyphenate);
  }

  private getOrder(node: ParagraphTextMediaBoxData): TextMediaBox.Order | null {
    if (!node?.attributes.field_media_posistion_mediabox) {
      return null;
    }

    return node.attributes.field_media_posistion_mediabox === 'left'
      ? TextMediaBox.Order.MEDIA_TEXT
      : TextMediaBox.Order.TEXT_MEDIA;
  }

  private getLayout(node: ParagraphTextMediaBoxData): TextMediaBox.Layout {
    if (!node.attributes.field_layout) {
      return TextMediaBox.Layout.STANDARD;
    }

    switch (node.attributes.field_layout) {
      case 'squared':
        return TextMediaBox.Layout.SQUARED;
      case 'big-image':
        return TextMediaBox.Layout.BIG_IMAGE;
      default:
        return TextMediaBox.Layout.STANDARD;
    }
  }

  private getBackground(node: ParagraphTextMediaBoxData): boolean {
    const setting = node.attributes?.field_gray_layer ?? 'true';

    return setting === 'true';
  }

  private getMediaConfig(
    node: ParagraphTextMediaBoxData
  ):
    | TextMediaBox.MediaImageConfig
    | TextMediaBox.MediaYoutubeVideoConfig
    | TextMediaBox.MediaVideoConfig
    | TextMediaBox.MediaAudioConfig {
    if (!node?.relationships?.field_media_mediabox?.data) {
      return null;
    }

    const { type, id } = node.relationships.field_media_mediabox.data;

    switch (type) {
      case MediaImageData.TypeEnum.MediaImage:
        const { dataUrls, metas } = this.getMediaDataUrls(id, type as TMediaNodeType, null, true);
        return {
          type: MediaTypesEnum.IMAGE,
          dataUrls,
          metas,
          fill: true,
          sizes: MediaResolver.buildImageConfigSizes(CmsComponentTypeEnum.TEXT_MEDIA_BOX),
        };

      case CmsMediaTypeEnum.RemoteVideo:
        const remoteVideoNode = this.getNode<MediaRemoteVideoData>(id, type);
        return new YoutubeVideo(remoteVideoNode).getComponentConfig();

      case CmsMediaTypeEnum.Video:
        return this.getMediaVideoConfig(id);

      case CmsMediaTypeEnum.Audio:
        return this.getMediaAudioConfig(id);

      default:
        return null;
    }
  }

  private getMediaVideoConfig(id: string): TextMediaBox.MediaVideoConfig {
    const mediaVideoNode = this.getNode<MediaVideoData>(id, CmsMediaTypeEnum.Video);

    const thumbnailNode = this.getNode<FileFileData>(
      mediaVideoNode?.relationships?.thumbnail?.data?.id,
      FileFileData.TypeEnum.FileFile
    );

    let thumbnail: string = null;

    if (thumbnailNode?.attributes?.image_style_uri) {
      thumbnail =
        thumbnailNode.attributes.image_style_uri['webp'] || thumbnailNode.attributes.image_style_uri['thumbnail'] || null;
    }

    const fileNode = this.getNode<FileFileData>(
      mediaVideoNode?.relationships?.field_media_video_file?.data?.id,
      FileFileData.TypeEnum.FileFile
    );

    if (!fileNode?.attributes?.uri?.url) {
      return null;
    }

    return {
      type: MediaTypesEnum.VIDEO,
      url: fileNode.attributes.uri.url,
      caption: null,
      metas: null,
      thumbnail,
    };
  }

  private getMediaAudioConfig(id: string): TextMediaBox.MediaAudioConfig {
    const mediaAudioNode = this.getNode<MediaAudioData>(id, CmsMediaTypeEnum.Audio);
    const fileNodeId = mediaAudioNode?.relationships?.field_media_audio_file?.data?.id;

    if (!fileNodeId) {
      return null;
    }

    const fileNode = this.getNode<FileFileData>(
      mediaAudioNode?.relationships?.field_media_audio_file?.data?.id,
      FileFileData.TypeEnum.FileFile
    );

    return {
      type: MediaTypesEnum.AUDIO,
      url: fileNode?.attributes?.uri?.url ?? null,
      fileNodeId,
      metas: {
        title: mediaAudioNode.attributes.field_title ?? 'Podcast',
        description: mediaAudioNode.attributes.field_description ?? null,
      },
    };
  }
}